import { ReactElement, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { formatTimeWithSeconds, getComparator, stableSort } from "../../../system/utils";
import StyledTableRow from "../../../components/styledTableRow";
import { IColumn, IStepHistory, Order } from "../../../types";
import TableContainer  from "../../../components/tableContainer";

const columns: IColumn[] = [
  {
    id: "step",
    label: "Steps",
    minWidth: 200
  },
  {
    id: "startTime",
    label: "Start Time",
    minWidth: 120,
    format: value => formatTimeWithSeconds(value)
  },
  {
    id: "endTime",
    label: "End Time",
    minWidth: 120,
    format: value => formatTimeWithSeconds(value)
  },
  {
    id: "residenceTime",
    label: "Residence Time",
    minWidth: 100,
    align: "right",
  },
  {
    id: "kwh",
    label: "KWH",
    minWidth: 100,
    align: "right",
  },
  {
    id: "Nitrogen",
    label: "nitrogen",
    minWidth: 100,
    align: "right",
  },
  {
    id: "Argon",
    label: "argon",
    minWidth: 100,
    align: "right",
  }
];

interface IProps {
  data: IStepHistory[];
}

const StepPanel = ({ data }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IStepHistory>("startTime");

  const createSortHandler = (property: keyof IStepHistory) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  let kwhTotal = 0;
  let nitrogenTotal = 0;
  let argonTotal = 0;
  let residenceTimeTotal = 0;

  return (
    <div>
      <TableContainer>
        <Table stickyHeader={true} size={"small"} >
          <TableHead>
            <TableRow>
              {columns.map((column) =>
                <TableCell
                  className="table-header"
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, width: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id as keyof IStepHistory)}
                  >
                    {column.label}
                    {orderBy === column.id
                      ? <span className="cell-visuallyHidden">
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </span>
                      : null}
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell className="table-header"/>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort<IStepHistory>(data, getComparator(order, orderBy))
              .map((row, index) => {

                kwhTotal += row.kwh ?? 0;
                nitrogenTotal += row.nitrogen ?? 0;
                argonTotal += row.argon ?? 0;
                residenceTimeTotal += row.residenceTime ?? 0;

                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}>
                    {columns.map((column: IColumn) => {
                      const value = row[column.id as keyof IStepHistory];

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                    <TableCell/>
                  </StyledTableRow>
                );
              })}
            <StyledTableRow hover={true}>
              <TableCell colSpan={3} align={"right"}>Total</TableCell>
              <TableCell align={"right"}>{residenceTimeTotal}</TableCell>
              <TableCell align={"right"}>{kwhTotal}</TableCell>
              <TableCell align={"right"}>{nitrogenTotal}</TableCell>
              <TableCell align={"right"}>{argonTotal}</TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default StepPanel;
